import { RefObject, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import Player from '../../player';
import { getPreviewHeight } from '../helpers';
import { uploadedImagesStyles } from 'components/uploaded-images/styles/styles';

interface PlayerWithCachedHeight {
  containerWidth: number;
  uploadId: string;
  url: string;
  containerRef: RefObject<HTMLDivElement>;
  previewUrl?: string;
  videoHeightCacheRef?: RefObject<Record<string, number>>;
}

export const PlayerWithCachedHeight = ({
  containerWidth,
  uploadId,
  previewUrl,
  url,
  containerRef,
  videoHeightCacheRef,
}: PlayerWithCachedHeight) => {
  const [height] = useState<number | null>(
    uploadId && videoHeightCacheRef?.current?.[uploadId]
      ? videoHeightCacheRef.current[uploadId]
      : null
  );

  useEffect(() => {
    // This logic only applies to the "activity_feed" scroll context where video height needs to be cached.
    // It is necessary because when scrolling from bottom to top in the activity feed virtualizer (TanStack),
    // the virtualizer must know the exact height of the video to prevent scroll jumps and layout glitches.
    // In all other usages of this component, videoHeightCacheRef is not passed, so this caching logic is skipped.
    if (
      !containerWidth ||
      !uploadId ||
      !previewUrl ||
      !videoHeightCacheRef ||
      videoHeightCacheRef.current?.[uploadId]
    ) {
      return;
    }

    getPreviewHeight(previewUrl, containerWidth)
      .then((calculatedHeight) => {
        videoHeightCacheRef.current[uploadId] = calculatedHeight;
      })
      .catch((err) => {
        console.error('Failed to load preview image for height calculation', err);
      });
  }, [containerWidth, uploadId, previewUrl, videoHeightCacheRef]);

  return (
    <Grid
      ref={containerRef}
      sx={uploadedImagesStyles.bigImageContainer}
      style={height ? { height: `${height}px` } : undefined}
    >
      {containerWidth ? <Player url={url} controls={true} /> : null}
    </Grid>
  );
};
